import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import PageContainer from '../components/layout/PageContainer';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { app } from '../firebaseConfig';
import InputMask from 'react-input-mask';
import { Listbox } from '@headlessui/react';

const genderOptions = [
  { value: '', label: 'Selecione' },
  { value: 'male', label: 'Homem' },
  { value: 'female', label: 'Mulher' },
];

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: '',
    birthDate: '',
    cpf: '',
    gender: '',
  });
  const [birthDateError, setBirthDateError] = useState('');

  const formatDateForDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const formatDateForStorage = (dateStr: string) => {
    if (!dateStr) return '';
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month}-${day}`;
  };

  const isValidDate = (dateStr: string) => {
    if (!dateStr) return false;
    
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return false;
    
    const [day, month, year] = dateStr.split('/').map(Number);
    
    const date = new Date(year, month - 1, day);
    return date.getDate() === day && 
           date.getMonth() === month - 1 && 
           date.getFullYear() === year &&
           year >= 1900 && 
           year <= new Date().getFullYear();
  };

  const handleBirthDateBlur = () => {
    if (formData.birthDate && !isValidDate(formData.birthDate)) {
      setBirthDateError('Data inválida');
    } else {
      setBirthDateError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (birthDateError) return;
    
    if (user) {
      const updatedUser = {
        ...user,
        ...formData,
        onboardingCompleted: true
      };

      setUser(updatedUser);

      const db = getFirestore(app);
      const userRef = doc(db, 'users', user.uid);
      const cleanCPF = formData.cpf.replace(/\D/g, '');
      const cleanPhone = formData.phone.replace(/\D/g, '');
      
      await updateDoc(userRef, {
        ...formData,
        cpf: cleanCPF,
        phone: cleanPhone,
        birthDate: formatDateForStorage(formData.birthDate),
        onboardingCompleted: true
      });

      navigate('/profile');
    }
  };

  return (
    <PageContainer>
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-lg rounded-xl p-6"
          >
            <h1 className="text-2xl font-bold text-white mb-6">Complete seu perfil</h1>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white/60 mb-1">
                  Nome completo
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-white/60 mb-1">
                  Telefone
                </label>
                <InputMask
                  mask="(99) 99999-9999"
                  value={formData.phone || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, phone: e.target.value || '' })}
                >
                  {(inputProps: any) => (
                    <input
                      {...inputProps}
                      type="tel"
                      id="phone"
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="(99) 99999-9999"
                      required
                    />
                  )}
                </InputMask>
              </div>

              <div>
                <label htmlFor="cpf" className="block text-sm font-medium text-white/60 mb-1">
                  CPF
                </label>
                <InputMask
                  mask="999.999.999-99"
                  value={formData.cpf || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, cpf: e.target.value || '' })}
                >
                  {(inputProps: any) => (
                    <input
                      {...inputProps}
                      type="text"
                      id="cpf"
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="000.000.000-00"
                      required
                    />
                  )}
                </InputMask>
              </div>

              <div>
                <label htmlFor="gender" className="block text-sm font-medium text-white/60 mb-1">
                  Gênero
                </label>
                <Listbox value={formData.gender} onChange={value => setFormData({ ...formData, gender: value })}>
                  <div className="relative">
                    <Listbox.Button className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-left focus:outline-none focus:ring-2 focus:ring-primary">
                      {genderOptions.find(opt => opt.value === formData.gender)?.label || 'Selecione'}
                    </Listbox.Button>
                    <Listbox.Options className="absolute mt-1 w-full bg-zinc-900/90 text-white rounded-lg shadow-lg z-10">
                      {genderOptions.map(option => (
                        <Listbox.Option
                          key={option.value}
                          value={option.value}
                          className={({ active }) =>
                            `cursor-pointer select-none px-4 py-2 ${active ? 'bg-primary/60 text-white' : ''}`
                          }
                        >
                          {option.label}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </div>
                </Listbox>
              </div>

              <div>
                <label htmlFor="birthDate" className="block text-sm font-medium text-white/60 mb-1">
                  Data de nascimento
                </label>
                <InputMask
                  mask="99/99/9999"
                  value={formData.birthDate || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, birthDate: e.target.value || '' })}
                  onBlur={handleBirthDateBlur}
                >
                  {(inputProps: any) => (
                    <input
                      {...inputProps}
                      type="text"
                      id="birthDate"
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="DD/MM/AAAA"
                      required
                    />
                  )}
                </InputMask>
                {birthDateError && <span className="text-red-400 text-sm">{birthDateError}</span>}
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                Continuar
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </PageContainer>
  );
};

export default OnboardingPage; 